import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Lock, Loader2, LogOut, Phone, Mail, Search, Download, Inbox, RefreshCw, MessageCircle,
} from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { adminLogin, fetchEnquiries, updateEnquiryStatus, formatApiError } from "@/lib/api";

const TOKEN_KEY = "sks_admin_token";
const STATUSES = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "processing", label: "Processing" },
  { key: "completed", label: "Completed" },
];

const badgeCls = {
  new: "bg-[#FFF3E0] text-[#E65100]",
  processing: "bg-[#E3F2FD] text-[#1565C0]",
  completed: "bg-[#E8F5E9] text-[#1B5E20]",
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginState, setLoginState] = useState({ status: "idle", error: "" });
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = useCallback(
    (t = token, f = filter) => {
      if (!t) return;
      setLoading(true);
      fetchEnquiries(t, f)
        .then(setEnquiries)
        .catch((e) => {
          if (e?.response?.status === 401) {
            localStorage.removeItem(TOKEN_KEY);
            setToken("");
          }
        })
        .finally(() => setLoading(false));
    },
    [token, filter]
  );

  useEffect(() => {
    if (token) load(token, filter);
  }, [token, filter, load]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginState({ status: "loading", error: "" });
    try {
      const data = await adminLogin(email, password);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setLoginState({ status: "error", error: formatApiError(err, "Login failed") });
    }
  };

  const setStatus = async (id, status) => {
    try {
      await updateEnquiryStatus(token, id, status);
      setEnquiries((prev) => prev.map((en) => (en.id === id ? { ...en, status } : en)));
    } catch {
      /* keep silent; next refresh reconciles */
    }
  };

  const exportCsv = () => {
    const rows = [["Name", "Mobile", "Email", "Category", "Message", "Items", "Status", "Date"]];
    filtered.forEach((en) => {
      rows.push([
        en.name, en.mobile, en.email || "", en.category || "", (en.message || "").replace(/\n/g, " "),
        (en.items || []).map((i) => `${i.name} x${i.qty}`).join("; "),
        en.status, en.created_at,
      ]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "shivam-kirana-enquiries.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = enquiries.filter(
    (en) =>
      en.name.toLowerCase().includes(search.toLowerCase()) || en.mobile.includes(search)
  );

  if (!token) {
    return (
      <main className="bg-bone min-h-[70vh] flex items-center justify-center px-4 py-16" data-testid="admin-login-page">
        <Reveal className="w-full max-w-md">
          <form onSubmit={handleLogin} className="rounded-3xl border border-line bg-white p-8 sm:p-10">
            <span className="w-12 h-12 rounded-full bg-forest/10 flex items-center justify-center">
              <Lock className="w-5 h-5 text-forest" />
            </span>
            <h1 className="mt-5 font-serif font-bold text-2xl text-ink">Store Admin</h1>
            <p className="mt-1 text-sm text-ink-muted">Sign in to view and manage customer enquiries.</p>
            <div className="mt-6 space-y-4">
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Admin email"
                className="w-full rounded-xl border border-line bg-bone px-4 py-3 text-sm outline-none focus:border-forest focus:ring-2 focus:ring-forest/15"
                data-testid="admin-login-email-input"
              />
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full rounded-xl border border-line bg-bone px-4 py-3 text-sm outline-none focus:border-forest focus:ring-2 focus:ring-forest/15"
                data-testid="admin-login-pin-input"
              />
            </div>
            {loginState.status === "error" && (
              <p className="mt-3 text-sm text-red-600" data-testid="admin-login-error">{loginState.error}</p>
            )}
            <button
              type="submit" disabled={loginState.status === "loading"}
              className="mt-6 w-full flex items-center justify-center gap-2 rounded-full bg-forest text-bone py-3.5 text-sm font-bold hover:bg-forest-deep transition-colors disabled:opacity-60"
              data-testid="admin-login-button"
            >
              {loginState.status === "loading" && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign In
            </button>
          </form>
        </Reveal>
      </main>
    );
  }

  return (
    <main className="bg-bone min-h-screen" data-testid="admin-dashboard">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-24">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Eyebrow>Store Admin</Eyebrow>
            <h1 className="mt-3 font-serif font-bold text-3xl sm:text-4xl tracking-tight text-ink">Customer Enquiries</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load()} className="p-2.5 rounded-full border border-line bg-white hover:bg-cream transition-colors" data-testid="admin-refresh-button" aria-label="Refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={exportCsv} className="flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2.5 text-xs font-bold hover:bg-cream transition-colors" data-testid="admin-export-button">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button
              onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }}
              className="flex items-center gap-2 rounded-full bg-forest-dark text-bone px-4 py-2.5 text-xs font-bold hover:bg-forest transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex flex-wrap gap-2" data-testid="admin-enquiry-status-filter">
            {STATUSES.map((s) => (
              <button
                key={s.key}
                onClick={() => setFilter(s.key)}
                className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${
                  filter === s.key ? "bg-forest text-bone" : "bg-white border border-line text-ink-soft hover:text-forest"
                }`}
                data-testid={`admin-filter-${s.key}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="relative sm:ml-auto sm:w-72">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or mobile…"
              className="w-full rounded-full border border-line bg-white pl-11 pr-4 py-2.5 text-sm outline-none focus:border-forest focus:ring-2 focus:ring-forest/15"
              data-testid="admin-search-input"
            />
          </div>
        </div>

        <div className="mt-8 space-y-4">
          {loading && enquiries.length === 0 ? (
            <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 text-forest animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center rounded-3xl border border-dashed border-line" data-testid="admin-empty-state">
              <Inbox className="w-10 h-10 text-line mx-auto" />
              <p className="mt-4 font-serif text-xl text-ink-soft">No enquiries here yet.</p>
            </div>
          ) : (
            <AnimatePresence>
              {filtered.map((en) => (
                <motion.article
                  key={en.id} layout initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="rounded-2xl border border-line bg-white p-6"
                  data-testid={`admin-enquiry-${en.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-serif font-bold text-lg text-ink">{en.name}</h3>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${badgeCls[en.status] || badgeCls.new}`}>
                          {en.status}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-ink-muted">{new Date(en.created_at).toLocaleString("en-IN")}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <a href={`tel:${en.mobile}`} className="p-2.5 rounded-full bg-forest/10 text-forest hover:bg-forest hover:text-bone transition-colors" data-testid={`admin-call-${en.id}`} aria-label="Call">
                        <Phone className="w-4 h-4" />
                      </a>
                      <a href={`https://wa.me/91${en.mobile.replace(/\D/g, "").slice(-10)}`} target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-full bg-forest/10 text-forest hover:bg-forest hover:text-bone transition-colors" data-testid={`admin-whatsapp-${en.id}`} aria-label="WhatsApp">
                        <MessageCircle className="w-4 h-4" />
                      </a>
                      {en.email && (
                        <a href={`mailto:${en.email}`} className="p-2.5 rounded-full bg-forest/10 text-forest hover:bg-forest hover:text-bone transition-colors" aria-label="Email">
                          <Mail className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-1 text-ink-soft">
                      <p><span className="font-semibold text-ink">Mobile:</span> {en.mobile}</p>
                      {en.email && <p><span className="font-semibold text-ink">Email:</span> {en.email}</p>}
                      {en.category && <p><span className="font-semibold text-ink">Category:</span> {en.category}</p>}
                      {en.message && <p className="pt-1"><span className="font-semibold text-ink">Message:</span> {en.message}</p>}
                    </div>
                    {en.items?.length > 0 && (
                      <ul className="rounded-xl bg-cream p-4 space-y-1 text-xs text-ink-soft">
                        {en.items.map((i, idx) => (
                          <li key={idx} className="flex justify-between gap-3">
                            <span>{i.name} · {i.unit}</span>
                            <span className="font-bold text-ink">x{i.qty}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {["new", "processing", "completed"].filter((s) => s !== en.status).map((s) => (
                      <button
                        key={s} onClick={() => setStatus(en.id, s)}
                        className="rounded-full border border-line px-3.5 py-1.5 text-[11px] font-bold text-ink-soft hover:border-forest hover:text-forest transition-colors"
                        data-testid={`admin-mark-${s}-${en.id}`}
                      >
                        Mark {s}
                      </button>
                    ))}
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          )}
        </div>
      </section>
    </main>
  );
}
